import {
  OfferingTypesEnum,
  ProductResWithMandatory,
} from '@monetize/types/app';
import { ExperienceConfigurationKeys } from '@monetize/types/experience-config';
import {
  FunctionComponent as FC,
  Fragment,
  useCallback,
  useState,
} from 'react';
import { MdSettings } from 'react-icons/md';
import {
  MAlert,
  MBox,
  MButton,
  MCheckbox,
  MDivider,
  MFlex,
  MGrid,
  MText,
} from '../../../../components/Monetize';
import { useExperienceConfiguration } from '../../../../hooks';
import { colors } from '../../../../styles/theme';
import DraggableProductRow from './DraggableProductRow';
import HeaderCellText from './HeaderCellText';
import OfferingHeaderCell from './OfferingHeaderCell';
import ProductAdvancedSettingsDrawer from './ProductAdvancedSettingsDrawer';
import ProductSearch from './ProductSearch';
import { productHasAdvancedSettings } from './products.utils';

export const getRowColor = (index: number) =>
  index % 2 === 0 ? 'tWhite.titanWhite' : 'tWhite.base';

const OFFERING_TYPES_WITHOUT_ADVANCED_SETTINGS = new Set([
  OfferingTypesEnum.MIN_COMMIT,
  OfferingTypesEnum.CUSTOM_PERCENT_OF_TOTAL,
  OfferingTypesEnum.ONETIME,
  OfferingTypesEnum.ONETIME_TOKEN,
]);

interface ProductsTableProps {
  isValid: boolean;
  products: ProductResWithMandatory[];
  isEditable: boolean;
  offeringType: OfferingTypesEnum;
  isReadOnly: boolean;
  isAllProductMandatory: boolean;
  showInactiveProductsBanner?: boolean;
  productBucket?: string;
  handleSelect: (product: ProductResWithMandatory) => void;
  handleRemove: (product: ProductResWithMandatory, i: number) => void;
  onMandatoryCheckboxChange: (product?: ProductResWithMandatory) => void;
  onUpdateAdvancedSettingsProducts: (
    updatedProducts: ProductResWithMandatory[],
  ) => void;
  onReorder: (updatedProducts: ProductResWithMandatory[]) => void;
  onSaveReorderedProducts: () => Promise<void>;
}

const ProductsTable: FC<ProductsTableProps> = ({
  isValid,
  products,
  isEditable,
  offeringType,
  isReadOnly,
  isAllProductMandatory,
  showInactiveProductsBanner,
  productBucket,
  handleSelect,
  handleRemove,
  onMandatoryCheckboxChange,
  onUpdateAdvancedSettingsProducts,
  onReorder,
  onSaveReorderedProducts,
}) => {
  const { allowQuantityDropdown, enablePriceTierLockEligible } =
    useExperienceConfiguration(
      ExperienceConfigurationKeys.quoteAndContractSettings,
    );

  const showPriceTierLock =
    enablePriceTierLockEligible &&
    !OFFERING_TYPES_WITHOUT_ADVANCED_SETTINGS.has(offeringType);

  const showAdvancedSettings = allowQuantityDropdown || showPriceTierLock;

  const hasProductWithAdvancedSettings = products.some(
    productHasAdvancedSettings,
  );

  const [isAdvancedSettingsOpen, setIsAdvancedSettingsOpen] = useState(false);

  const handleOpenAdvancedSettings = () => {
    setIsAdvancedSettingsOpen(true);
  };

  const handleCancelAdvancedSettings = () => {
    setIsAdvancedSettingsOpen(false);
  };

  const isIndeterminate =
    products.some(({ isMandatory }) => isMandatory) && !isAllProductMandatory;

  const isDisabled = !isEditable || isReadOnly;

  const moveProduct = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      const updated = [...products];
      const [removed] = updated.splice(dragIndex, 1);
      updated.splice(hoverIndex, 0, removed);
      onReorder(updated);
    },
    [products],
  );

  const saveProductReorder = useCallback(() => {
    onSaveReorderedProducts();
  }, [onSaveReorderedProducts]);

  const rowColumns =
    showAdvancedSettings && hasProductWithAdvancedSettings
      ? '0.1fr 0.9fr 0.9fr 0.9fr 1.1fr 0.9fr minmax(auto,0.5fr) 0.1fr'
      : '0.1fr 0.9fr 0.9fr 0.9fr 1.1fr 0.9fr 0.1fr';

  return (
    <>
      <MText
        width="full"
        alignSelf="start"
        fontSize="lg"
        fontWeight="bold"
        color="tPurple.base"
        mb="3"
        ml={8}
      >
        Products
      </MText>

      <MBox w="full" data-testid="offering-form-product-list" pl={8}>
        <MBox
          borderWidth="1px"
          borderStyle="solid"
          borderColor="border.subtle"
          borderRadius="lg"
          background={colors.tWhite.base}
          py={4}
          px={6}
        >
          <MGrid
            gridTemplateColumns="120px 1fr auto"
            columnGap={2}
            w="full"
            alignItems="center"
          >
            <MText fontWeight="bold" fontSize="md">
              Add Products
            </MText>

            <ProductSearch
              productBucket={productBucket}
              isDisabled={isDisabled}
              onSelect={handleSelect}
              selected={products}
              offeringType={offeringType}
              mb={4}
            />

            {products.length > 0 && showAdvancedSettings && (
              <MButton
                variant="tertiary"
                size="sm"
                leftIcon={<MdSettings />}
                onClick={handleOpenAdvancedSettings}
                mb={4}
              >
                Configure
              </MButton>
            )}
          </MGrid>

          {products.length > 0 && (
            <MBox mt={4}>
              {showInactiveProductsBanner && (
                <MAlert
                  alertIconProps={{ boxSize: 4 }}
                  alertDescriptionProps={{ fontSize: 'sm' }}
                  alignSelf="stretch"
                  type="warning"
                >
                  There are inactive products in this offering. Either remove
                  them or set them to active before saving this offering.
                </MAlert>
              )}
              <MGrid
                gridTemplateColumns={rowColumns}
                w="full"
                alignItems="center"
              >
                <MDivider gridColumn="1 / -1" mb={2} />

                <OfferingHeaderCell py={2}>
                  {products.length > 1 && <MBox boxSize={5} />}
                </OfferingHeaderCell>

                <OfferingHeaderCell py={2}>
                  <HeaderCellText
                    textProps={{ fontSize: 'sm', textTransform: 'uppercase' }}
                  >
                    Products
                  </HeaderCellText>
                </OfferingHeaderCell>

                <OfferingHeaderCell py={2}>
                  <HeaderCellText
                    textProps={{ fontSize: 'sm', textTransform: 'uppercase' }}
                  >
                    Product Type
                  </HeaderCellText>
                </OfferingHeaderCell>

                <OfferingHeaderCell py={2}>
                  <HeaderCellText
                    textProps={{ fontSize: 'sm', textTransform: 'uppercase' }}
                  >
                    Description
                  </HeaderCellText>
                </OfferingHeaderCell>

                <OfferingHeaderCell py={2}>
                  <HeaderCellText
                    textProps={{ fontSize: 'sm', textTransform: 'uppercase' }}
                  >
                    Status
                  </HeaderCellText>
                </OfferingHeaderCell>

                <OfferingHeaderCell py={2}>
                  <HeaderCellText
                    textProps={{ fontSize: 'sm', textTransform: 'uppercase' }}
                    tooltipLabel="Unchecked items are optional on quote."
                    justifyContent="center"
                    additionalNode={
                      !isDisabled && (
                        <MFlex w="full" justifyContent="center">
                          <MCheckbox
                            isDisabled={isDisabled}
                            isChecked={isAllProductMandatory}
                            onChange={() => onMandatoryCheckboxChange()}
                            isIndeterminate={isIndeterminate}
                          >
                            <MBox as="span">Select All</MBox>
                          </MCheckbox>
                        </MFlex>
                      )
                    }
                  >
                    Required
                  </HeaderCellText>
                </OfferingHeaderCell>

                {showAdvancedSettings && hasProductWithAdvancedSettings && (
                  <OfferingHeaderCell py={2}>
                    <HeaderCellText
                      textProps={{ fontSize: 'sm', textTransform: 'uppercase' }}
                      justifyContent="center"
                      tooltipLabel="Advanced settings that apply to the product in the quote created from this offering."
                    >
                      Configurations
                    </HeaderCellText>
                  </OfferingHeaderCell>
                )}

                <OfferingHeaderCell>
                  {!isReadOnly && isEditable && <MBox mr={2} boxSize={5} />}
                </OfferingHeaderCell>

                <MDivider gridColumn="1 / -1" />
              </MGrid>

              <MBox data-testid="offering-product-list">
                {products.map((product, index) => (
                  <Fragment key={product.id}>
                    <DraggableProductRow
                      product={product}
                      index={index}
                      isEditable={isEditable}
                      isReadOnly={isReadOnly}
                      showDragHandle={products.length > 1}
                      showAdvancedSettings={showAdvancedSettings}
                      showPriceTierLock={showPriceTierLock}
                      allowQuantityDropdown={allowQuantityDropdown}
                      totalColumns={rowColumns}
                      onMandatoryCheckboxChange={onMandatoryCheckboxChange}
                      onOpenAdvancedSettings={handleOpenAdvancedSettings}
                      isDisabled={isDisabled}
                      moveProduct={moveProduct}
                      saveProductReorder={saveProductReorder}
                      onRemove={handleRemove}
                      getRowColor={getRowColor}
                      hasProductWithAdvancedSettings={
                        hasProductWithAdvancedSettings
                      }
                    />
                    <MDivider
                      bgColor={getRowColor(index)}
                      gridColumn="1 / -1"
                    />
                  </Fragment>
                ))}
              </MBox>
            </MBox>
          )}
        </MBox>
      </MBox>

      {isAdvancedSettingsOpen && (
        <ProductAdvancedSettingsDrawer
          allowQuantityDropdown={allowQuantityDropdown}
          enablePriceTierLockEligible={showPriceTierLock}
          products={products}
          isReadOnly={isReadOnly}
          isOpen={isAdvancedSettingsOpen}
          onClose={handleCancelAdvancedSettings}
          onUpdateProducts={onUpdateAdvancedSettingsProducts}
        />
      )}
    </>
  );
};

export default ProductsTable;
